/**
 * Pricing configuration grid shared by device and feature billing —
 * lets a billing admin set a default price per pricing row (device
 * category or billable feature) plus per-site-group/site overrides,
 * either against Redux/API state (an account's saved config) or as a
 * controlled value (inline forms like customer creation).
 *
 * The device/feature difference lives entirely in the descriptor
 * passed by the two thin wrappers (`DevicePricingConfigGrid`,
 * `FeaturePricingConfigGrid`): row identity, the pricing-unit choices,
 * the save payload shape, and the extra/gating columns each type
 * needs.
 */
import { Alert, Box, Button, ListItemText, Menu, MenuItem } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import DataGridToolbar from '@sophocles/data-grids/components/toolbar';
import {
  addPricingConfigOverride,
  cancelPricingConfigEdit,
  removePricingConfigOverride,
  selectPricingConfigEditMode,
  selectPricingConfigHasChanges,
  selectPricingConfigLoading,
  selectPricingConfigs,
  selectPricingConfigSaving,
  setPricingConfigEditMode,
  updatePricingConfig,
  updatePricingConfigOverride,
} from '@sophocles/features/billing/pricingConfigSlice';
import AdjustPriceDialog from '@sophocles/views/billing/dialogs/AdjustPriceDialog';
import OverridesDialog from '@sophocles/views/billing/dialogs/OverridesDialog';

/**
 * @param {Object} descriptor
 * @param {'device'|'feature'} descriptor.configType
 * @param {function} descriptor.getRowKey - row -> the id used for lookups/dispatch (category id or feature key)
 * @param {function} descriptor.fetchDefaultTemplate - controlled-mode template loader
 * @param {function} descriptor.loadAction - thunk to load the account's saved config
 * @param {function} descriptor.saveAction - thunk to save the account's config
 * @param {function} descriptor.buildSaveConfigEntry - config -> the entry shape the save API expects
 * @param {function} descriptor.entityLabel - config -> display name for dialogs
 * @param {function} descriptor.buildColumns - builds the DataGrid column set (device/feature differ structurally)
 * @param {string} descriptor.loadTemplateFailedLogKey - i18n key for the console.error prefix
 * @param {string} descriptor.loadTemplateFailedMessageKey - i18n key for the localError fallback message
 */
function PricingConfigGrid({
  descriptor,
  accountId,
  customerId,
  renderToolbar,
  // Controlled mode props
  value,
  onChange,
  alwaysEditMode = false,
  // Extra props consumed only by buildColumns (e.g. device's isBilledVirtual/inventoryPolicy)
  ...columnProps
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { configType, getRowKey } = descriptor;

  // Determine if we're in controlled mode
  const isControlled = value !== undefined && onChange !== undefined;

  // Redux state (only used in non-controlled mode)
  const reduxConfigs = useSelector(selectPricingConfigs(configType));
  const reduxLoading = useSelector(selectPricingConfigLoading(configType));
  const reduxSaving = useSelector(selectPricingConfigSaving(configType));
  const reduxEditMode = useSelector(selectPricingConfigEditMode(configType));
  const reduxHasChanges = useSelector(selectPricingConfigHasChanges(configType));

  // Local state for controlled mode
  const [localConfigs, setLocalConfigs] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localOriginalConfigs, setLocalOriginalConfigs] = useState([]);
  const [localEditMode, setLocalEditMode] = useState(alwaysEditMode);
  const [localError, setLocalError] = useState(null);

  // Use appropriate state based on mode
  const configs = isControlled ? value || localConfigs : reduxConfigs;
  const loading = isControlled ? localLoading : reduxLoading;
  const saving = isControlled ? false : reduxSaving;
  const editMode = isControlled ? alwaysEditMode || localEditMode : reduxEditMode;
  const hasChanges = isControlled
    ? JSON.stringify(value || localConfigs) !== JSON.stringify(localOriginalConfigs)
    : reduxHasChanges;

  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [overridesDialogOpen, setOverridesDialogOpen] = useState(false);

  // Row action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuConfig, setActionMenuConfig] = useState(null);

  // Adjust price dialog state
  const [adjustPriceDialogOpen, setAdjustPriceDialogOpen] = useState(false);
  const [adjustPriceConfig, setAdjustPriceConfig] = useState(null);

  // Load configs on mount
  useEffect(() => {
    if (isControlled) {
      // In controlled mode, load default template if no value provided
      if (!value || value.length === 0) {
        setLocalLoading(true);
        setLocalError(null);
        descriptor
          .fetchDefaultTemplate()
          .then(data => {
            const loadedConfigs = (data.configs || []).map(c => ({
              ...c,
              default_price: c.default_price ?? '0',
            }));
            setLocalConfigs(loadedConfigs);
            setLocalOriginalConfigs(JSON.parse(JSON.stringify(loadedConfigs)));
            if (onChange) onChange(loadedConfigs);
          })
          .catch(err => {
            console.error(t(descriptor.loadTemplateFailedLogKey), err);
            setLocalError(err.message || t(descriptor.loadTemplateFailedMessageKey));
          })
          .finally(() => {
            setLocalLoading(false);
          });
      } else {
        // Initialize original configs from provided value
        setLocalOriginalConfigs(JSON.parse(JSON.stringify(value)));
      }
    } else if (accountId) {
      dispatch(descriptor.loadAction(accountId));
    }
    // Intentionally not including value/onChange to avoid loops.
  }, [dispatch, accountId, isControlled]);

  // Helper to update configs in controlled mode
  const updateControlledConfigs = useCallback(
    updater => {
      if (isControlled) {
        const currentConfigs = value || localConfigs;
        const newConfigs = updater(currentConfigs);
        setLocalConfigs(newConfigs);
        if (onChange) onChange(newConfigs);
      }
    },
    [isControlled, value, localConfigs, onChange],
  );

  const handleEditClick = useCallback(() => {
    if (isControlled) {
      setLocalEditMode(true);
      setLocalOriginalConfigs(JSON.parse(JSON.stringify(value || localConfigs)));
    } else {
      dispatch(setPricingConfigEditMode({ configType, editMode: true }));
    }
  }, [dispatch, isControlled, value, localConfigs, configType]);

  const handleCancelClick = useCallback(() => {
    if (isControlled) {
      setLocalConfigs(JSON.parse(JSON.stringify(localOriginalConfigs)));
      if (onChange) onChange(JSON.parse(JSON.stringify(localOriginalConfigs)));
      setLocalEditMode(false);
    } else {
      dispatch(cancelPricingConfigEdit({ configType }));
    }
  }, [dispatch, isControlled, localOriginalConfigs, onChange, configType]);

  const handleSaveClick = useCallback(() => {
    if (isControlled) {
      setLocalOriginalConfigs(JSON.parse(JSON.stringify(value || localConfigs)));
      setLocalEditMode(false);
    } else {
      const configsToSave = configs.map(descriptor.buildSaveConfigEntry);
      dispatch(descriptor.saveAction({ accountId, configs: configsToSave }));
    }
  }, [dispatch, accountId, configs, isControlled, value, localConfigs, descriptor]);

  const handleUpdateConfig = useCallback(
    (rowKey, updates) => {
      if (isControlled) {
        updateControlledConfigs(currentConfigs =>
          currentConfigs.map(c => (getRowKey(c) === rowKey ? { ...c, ...updates } : c)),
        );
      } else {
        dispatch(updatePricingConfig({ configType, rowKey, updates }));
      }
    },
    [dispatch, isControlled, updateControlledConfigs, getRowKey, configType],
  );

  const handleAddOverride = useCallback(
    (rowKey, override) => {
      if (isControlled) {
        updateControlledConfigs(currentConfigs =>
          currentConfigs.map(c =>
            getRowKey(c) === rowKey ? { ...c, overrides: [...(c.overrides || []), override] } : c,
          ),
        );
      } else {
        dispatch(addPricingConfigOverride({ configType, rowKey, override }));
      }
    },
    [dispatch, isControlled, updateControlledConfigs, getRowKey, configType],
  );

  const handleRemoveOverride = useCallback(
    (rowKey, overrideIndex) => {
      if (isControlled) {
        updateControlledConfigs(currentConfigs =>
          currentConfigs.map(c =>
            getRowKey(c) === rowKey
              ? { ...c, overrides: (c.overrides || []).filter((_, i) => i !== overrideIndex) }
              : c,
          ),
        );
      } else {
        dispatch(removePricingConfigOverride({ configType, rowKey, overrideIndex }));
      }
    },
    [dispatch, isControlled, updateControlledConfigs, getRowKey, configType],
  );

  const handleUpdateOverride = useCallback(
    (rowKey, overrideIndex, updates) => {
      if (isControlled) {
        updateControlledConfigs(currentConfigs =>
          currentConfigs.map(c => {
            if (getRowKey(c) === rowKey && c.overrides?.[overrideIndex]) {
              const newOverrides = [...c.overrides];
              newOverrides[overrideIndex] = { ...newOverrides[overrideIndex], ...updates };
              return { ...c, overrides: newOverrides };
            }
            return c;
          }),
        );
      } else {
        dispatch(updatePricingConfigOverride({ configType, rowKey, overrideIndex, updates }));
      }
    },
    [dispatch, isControlled, updateControlledConfigs, getRowKey, configType],
  );

  const handleOpenOverrides = useCallback(
    config => {
      setSelectedRowKey(getRowKey(config));
      setOverridesDialogOpen(true);
    },
    [getRowKey],
  );

  // Row action menu handlers
  const handleActionMenuOpen = useCallback((event, config) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuConfig(config);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setActionMenuConfig(null);
  }, []);

  // Align pricing - removes all overrides, keeping only the default price
  const handleAlignPricing = useCallback(() => {
    if (actionMenuConfig) {
      handleUpdateConfig(getRowKey(actionMenuConfig), { overrides: [] });
    }
    handleActionMenuClose();
  }, [actionMenuConfig, handleActionMenuClose, handleUpdateConfig, getRowKey]);

  // Open adjust price dialog
  const handleOpenAdjustPrice = useCallback(() => {
    setAdjustPriceConfig(actionMenuConfig);
    setAdjustPriceDialogOpen(true);
    handleActionMenuClose();
  }, [actionMenuConfig, handleActionMenuClose]);

  // Apply price adjustment to default price and all overrides
  const handleApplyPriceAdjustment = useCallback(
    adjustment => {
      if (!adjustPriceConfig) return;

      const currentDefaultPrice = parseFloat(adjustPriceConfig.default_price || 0);
      const currentOverrides = adjustPriceConfig.overrides || [];

      const applyAdjustment = currentPrice => {
        if (adjustment.type === 'amount') {
          return Math.max(0, currentPrice + adjustment.value);
        }
        const multiplier = 1 + adjustment.value / 100;
        return Math.max(0, currentPrice * multiplier);
      };

      const newDefaultPrice = applyAdjustment(currentDefaultPrice);
      const newOverrides = currentOverrides.map(override => ({
        ...override,
        price: applyAdjustment(parseFloat(override.price || 0)).toFixed(2),
      }));

      handleUpdateConfig(getRowKey(adjustPriceConfig), {
        default_price: newDefaultPrice.toFixed(2),
        overrides: newOverrides,
      });

      setAdjustPriceDialogOpen(false);
      setAdjustPriceConfig(null);
    },
    [adjustPriceConfig, handleUpdateConfig, getRowKey],
  );

  // columnProps is a rest-spread object (a fresh reference every render), so
  // it's serialized for the memo key rather than compared by identity.
  const columnPropsKey = JSON.stringify(columnProps);
  const columns = useMemo(
    () =>
      descriptor.buildColumns({
        t,
        editMode,
        alwaysEditMode,
        onUpdateConfig: handleUpdateConfig,
        onOpenOverrides: handleOpenOverrides,
        onActionMenuOpen: handleActionMenuOpen,
        ...columnProps,
      }),
    [
      t,
      editMode,
      alwaysEditMode,
      handleUpdateConfig,
      handleOpenOverrides,
      handleActionMenuOpen,
      descriptor,
      columnPropsKey,
    ],
  );

  const rows = useMemo(() => {
    return configs.map((config, index) => ({
      ...config,
      id: getRowKey(config) ?? index,
    }));
  }, [configs, getRowKey]);

  // In alwaysEditMode, we don't show edit/save/cancel buttons
  const toolbarButtons = alwaysEditMode ? null : editMode ? (
    <>
      <Button variant="outlined" onClick={handleCancelClick} disabled={saving}>
        {t('billing.pricingConfig.cancel')}
      </Button>
      <Button variant="contained" onClick={handleSaveClick} disabled={saving || !hasChanges}>
        {saving ? t('billing.pricingConfig.saving') : t('billing.pricingConfig.saveChanges')}
      </Button>
    </>
  ) : (
    <Button variant="contained" onClick={handleEditClick}>
      {t('billing.pricingConfig.editConfiguration')}
    </Button>
  );

  return (
    <Box>
      {renderToolbar ? (
        renderToolbar(toolbarButtons)
      ) : (
        // Fallback when this grid is rendered standalone (no parent
        // toolbar): a flush DataGridToolbar with just the edit/save
        // controls on the right.  Keeps the chrome consistent with
        // every other list surface.
        <DataGridToolbar variant="flat" showQuickFilter={false} toolbarActions={toolbarButtons} />
      )}

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      <DataGrid
        key={descriptor.gridKey ? descriptor.gridKey(columnProps) : undefined}
        rows={rows}
        columns={columns}
        getRowId={row => getRowKey(row) ?? row.id}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnSelector
        disableColumnResize
        sortingMode="server"
        hideFooter
        sx={{
          '& .MuiDataGrid-columnHeader': {
            cursor: 'default',
          },
        }}
      />

      <OverridesDialog
        open={overridesDialogOpen}
        onClose={() => setOverridesDialogOpen(false)}
        rowKey={selectedRowKey}
        getRowKey={getRowKey}
        entityLabel={descriptor.entityLabel}
        configs={configs}
        editMode={editMode}
        onAddOverride={handleAddOverride}
        onRemoveOverride={handleRemoveOverride}
        onUpdateOverride={handleUpdateOverride}
        customerId={customerId}
      />

      {/* Row actions menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleOpenAdjustPrice}>
          <ListItemText
            primary={t('billing.pricingConfig.adjustPriceAction')}
            secondary={t('billing.pricingConfig.adjustPriceActionSecondary')}
          />
        </MenuItem>
        <MenuItem onClick={handleAlignPricing} disabled={!actionMenuConfig?.overrides?.length}>
          <ListItemText
            primary={t('billing.pricingConfig.alignPricingAction')}
            secondary={
              actionMenuConfig?.overrides?.length
                ? t('billing.pricingConfig.alignOverridesToRemove', { count: actionMenuConfig.overrides.length })
                : t('billing.pricingConfig.alignPricingNoOverrides')
            }
          />
        </MenuItem>
      </Menu>

      {/* Adjust price dialog */}
      <AdjustPriceDialog
        open={adjustPriceDialogOpen}
        onClose={() => {
          setAdjustPriceDialogOpen(false);
          setAdjustPriceConfig(null);
        }}
        entityLabel={adjustPriceConfig ? descriptor.entityLabel(adjustPriceConfig) : null}
        defaultPrice={adjustPriceConfig?.default_price}
        overridesCount={adjustPriceConfig?.overrides?.length || 0}
        onApply={handleApplyPriceAdjustment}
      />
    </Box>
  );
}

export default PricingConfigGrid;
